import React, { useState } from 'react';
import type { DNSRecord, DNSZone } from '../../../types/dns';
import DNSRecordListHeader from './record-list/DNSRecordListHeader';
import DNSRecordCard from './record-list/DNSRecordCard';
import DNSRecordListEmpty from './record-list/DNSRecordListEmpty';
import DNSRecordListError from './record-list/DNSRecordListError';
import DNSRecordListLoading from './record-list/DNSRecordListLoading';

interface DNSRecordsListProps {
  zone: DNSZone;
  records: DNSRecord[];
  loading?: boolean;
  error?: string | null;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onEditRecord?: (record: DNSRecord) => void;
  onAddRecord?: () => void;
  onRefresh: (zoneId: string) => void;
  bulkTTL: string;
  setBulkTTL: (value: string) => void;
  onBulkTTLUpdate: () => void;
  onBulkDeleteClick: () => void;
}

const DNSRecordsList: React.FC<DNSRecordsListProps> = ({
  zone,
  records,
  loading = false,
  error = null,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEditRecord,
  onAddRecord,
  onRefresh,
  bulkTTL,
  setBulkTTL,
  onBulkTTLUpdate,
  onBulkDeleteClick
}) => {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const copyToClipboard = async (text: string, recordId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(recordId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(recordId);
        setTimeout(() => setCopySuccess(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const allSelected = records.length > 0 && selectedIds.length === records.length;

  return (
    <div>
      <DNSRecordListHeader
        recordCount={records.length}
        selectedIds={selectedIds}
        allSelected={allSelected}
        onToggleSelectAll={onToggleSelectAll}
        onBulkDeleteClick={onBulkDeleteClick}
        onAddRecord={onAddRecord}
        bulkTTL={bulkTTL}
        setBulkTTL={setBulkTTL}
        onBulkTTLUpdate={onBulkTTLUpdate}
      />

      <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
        {error ? (
          <DNSRecordListError error={error} onRetry={() => onRefresh(zone.id)} />
        ) : loading ? (
          <DNSRecordListLoading />
        ) : records.length === 0 ? (
          <DNSRecordListEmpty onAddRecord={onAddRecord} />
        ) : (
          records.map((record, index) => (
            <DNSRecordCard
              key={record.id}
              record={record}
              zoneName={zone.name}
              isSelected={selectedIds.includes(record.id)}
              index={index}
              onToggleSelect={onToggleSelect}
              onEdit={onEditRecord}
              onCopy={copyToClipboard}
              copySuccess={copySuccess}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default DNSRecordsList;
